import { useMemo } from 'react';

import type { BaseballAverageStatType } from '@sorare/core/src/__generated__/globalTypes';
import {
  AverageLimit,
  AveragePlayerScore,
  Rarity,
  SeasonEligibility,
  So5LeaderboardSeasonality,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import type { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import type { PlayerSearchView } from '@sorare/core/src/lib/playerSearchView';
import {
  PlayerSearchViewAttributes,
  StatsDisplay,
  StatsMode,
  StatsOnboarding,
} from '@sorare/core/src/lib/playerSearchView';
import {
  baseballStatSorts,
  footballStatSorts,
  nbaStatSorts,
} from '@sorare/core/src/lib/scouting-center';

import {
  hittingBaseballAverageStats,
  pitchingBaseballAverageStats,
  reorderInput,
} from '#src/lib/scoutingCenter.ts';

const NEXT_STEPS = {
  [StatsOnboarding.INIT]: StatsOnboarding.ADVANCED_VIEW,
  [StatsOnboarding.ADVANCED_VIEW]: StatsOnboarding.SORT_BY_CLICKING,
  [StatsOnboarding.SORT_BY_CLICKING]: StatsOnboarding.COMBINE_SORTS,
  [StatsOnboarding.COMBINE_SORTS]: StatsOnboarding.FILTERS,
  [StatsOnboarding.FILTERS]: StatsOnboarding.ONBOARDED,
  [StatsOnboarding.ONBOARDED]: StatsOnboarding.INIT,
};

const displayableAverageStats = {
  [StatsMode.HITTING]: hittingBaseballAverageStats,
  [StatsMode.PITCHING]: pitchingBaseballAverageStats,
  [StatsMode.DEFAULT]: null,
};

const defaultAverageScoresBySport: Record<Sport, TotalAveragePlayerScore[]> = {
  [Sport.NBA]: [AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE],
  [Sport.FOOTBALL]: [
    AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE,
    AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
    AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE,
  ],
  [Sport.BASEBALL]: [
    AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
    AveragePlayerScore.SEASON_AVERAGE_SCORE,
  ],
};

const getDefaultStatsManager = (sport: Sport): PlayerSearchView => ({
  [PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE]:
    defaultAverageScoresBySport[sport],
  [PlayerSearchViewAttributes.BREAKDOWN]: [],
  [PlayerSearchViewAttributes.STATS]: [],
  [PlayerSearchViewAttributes.RARITY]: Rarity.limited,
  [PlayerSearchViewAttributes.SEASONALITY]: So5LeaderboardSeasonality.IN_SEASON,
  [PlayerSearchViewAttributes.STATS_DISPLAY]: StatsDisplay.PER_MATCH,
  [PlayerSearchViewAttributes.AVERAGE_LIMIT]: AverageLimit.LAST_10,
  [PlayerSearchViewAttributes.SORT]: '',
  [PlayerSearchViewAttributes.SHOW_APPEARANCES]: true,
  [PlayerSearchViewAttributes.PROJECTION]: [],
  [PlayerSearchViewAttributes.ONBOARDING]: StatsOnboarding.INIT,
  [PlayerSearchViewAttributes.STATS_MODE]:
    sport === Sport.BASEBALL ? StatsMode.HITTING : StatsMode.DEFAULT,
  [PlayerSearchViewAttributes.CARDS_SORT]: 'Cards On Sale Lowest Price',
  [PlayerSearchViewAttributes.PROJECTION_GRADE]: [],
});

export const useManagePlayerSearchView = () => {
  const { sport } = useSportContext();

  const { lifecycle, update } = useLifecycle();

  const allStatsManagers = lifecycle?.statsManager;
  const savedStatsManager = lifecycle?.statsManager?.[sport];

  const defaultStatsManager = getDefaultStatsManager(sport);

  const statsManager = useMemo(() => {
    const averagePlayerScores =
      savedStatsManager?.[PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE];
    const hasL10InAveragePlayerScores = averagePlayerScores?.includes(
      AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
    );

    const l10AveragePlayerScores: TotalAveragePlayerScore[] =
      averagePlayerScores
        ? averagePlayerScores
            .map(i =>
              i === AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE
                ? hasL10InAveragePlayerScores
                  ? false
                  : AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
                : i
            )
            .filter(Boolean)
        : [];

    const savedStatsManagerWithoutLast15 = {
      ...savedStatsManager,
      ...((averagePlayerScores || []).length > 0
        ? {
            [PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE]:
              l10AveragePlayerScores,
          }
        : {}),
      [PlayerSearchViewAttributes.BREAKDOWN]: (
        savedStatsManager?.[PlayerSearchViewAttributes.BREAKDOWN] || []
      ).filter(
        i =>
          i !== AveragePlayerScore.LAST_FIFTEEN_AVERAGE_ALL_AROUND_SCORE &&
          i !== AveragePlayerScore.LAST_FIFTEEN_AVERAGE_DECISIVE_SCORE
      ),
    };

    return {
      ...defaultStatsManager,
      ...savedStatsManagerWithoutLast15, // override default values with saved values
      ...(savedStatsManager?.[PlayerSearchViewAttributes.SEASONALITY] ===
        So5LeaderboardSeasonality.ALL_SEASONS && {
        [PlayerSearchViewAttributes.SEASONALITY]: SeasonEligibility.CLASSIC,
      }),
    };
  }, [defaultStatsManager, savedStatsManager]);

  const updatePlayerSearchView = (input: PlayerSearchView) => {
    update(LIFECYCLE.statsManager, {
      ...allStatsManagers,
      [sport]: {
        ...reorderInput(input, sport),
        [PlayerSearchViewAttributes.ONBOARDING]:
          statsManager[PlayerSearchViewAttributes.ONBOARDING],
      },
    });
  };

  const updatePlayerSearchViewAttribute = <
    T extends PlayerSearchViewAttributes,
  >(
    key: T,
    value: PlayerSearchView[T]
  ) => {
    updatePlayerSearchView({
      ...statsManager,
      [key]: value,
    });
  };

  const sawOnboarding = (step: StatsOnboarding) => {
    update(LIFECYCLE.statsManager, {
      ...allStatsManagers,
      [sport]: {
        ...allStatsManagers?.[sport],
        [PlayerSearchViewAttributes.ONBOARDING]: NEXT_STEPS[step],
      },
    });
  };

  const statsSorts = {
    [Sport.FOOTBALL]: footballStatSorts(
      statsManager[PlayerSearchViewAttributes.STATS_DISPLAY]
    ),
    [Sport.NBA]: nbaStatSorts,
    [Sport.BASEBALL]: baseballStatSorts,
  }[sport];

  const displayableStats = statsManager[PlayerSearchViewAttributes.STATS]
    ?.map(stat => {
      const statsMode = statsManager[PlayerSearchViewAttributes.STATS_MODE];
      if (sport === Sport.BASEBALL && statsMode !== StatsMode.DEFAULT) {
        if (
          !displayableAverageStats[statsMode].includes(
            stat as BaseballAverageStatType
          )
        )
          return null;
      }
      return stat;
    })
    .filter(Boolean);

  return {
    statsManager,
    displayableStats,
    statsSorts,
    defaultStatsManager,
    updatePlayerSearchView,
    updatePlayerSearchViewAttribute,
    sawOnboarding,
  };
};
